/**
 * Every physics constant, in one object (PRD N11).
 *
 * ⚠️ These values are the OWNER's to tune (CLAUDE.md §5). You may add a constant with
 * its source documented; you may not retune an existing one.
 *
 * Two rules that are easy to break:
 *
 *  1. **No constant here may be computed with a transcendental.** `Math.exp`, `Math.pow`
 *     and friends are not correctly rounded, and a constant computed at module load
 *     would differ between engines just as surely as one computed in the loop. Bake the
 *     literal in and record how it was derived. (ARCHITECTURE.md §5.5)
 *  2. **Changing any value here changes shot outcomes**, so it is a `physicsVersion`
 *     bump once levels exist (PRD N10). During Phase 1 nothing is published, so the
 *     version stays 1 and freezes at the Phase 1 gate.
 *
 * Starting values come from the brief §6, which draws on Han (2005) and Mathavan,
 * Jackson & Parkin (2010). Provenance and deviations: docs/physics.md.
 */

export interface PhysicsConfig {
  // --- Integration -----------------------------------------------------------
  /** Fixed timestep. Frame-rate independent by construction; never variable. */
  readonly dtS: number;
  /** Safety net. A shot that exceeds this has a bug; the result flags it. */
  readonly maxSimTimeS: number;
  /** Steps between recorded keyframes. 8 × (1/480 s) ≈ 60 Hz. */
  readonly keyframeStride: number;

  // --- World -----------------------------------------------------------------
  readonly gravityMps2: number;
  readonly ballRadiusM: number;
  readonly ballMassKg: number;

  // --- Ball ↔ cloth ----------------------------------------------------------
  /** Coefficient of sliding friction against the cloth. Consumed from PHY-001. */
  readonly clothSlidingFriction: number;
  /** Rolling resistance once the ball is rolling. Consumed from PHY-002. */
  readonly clothRollingResistance: number;
  /** Decay of spin about the vertical axis. Consumed from PHY-002. */
  readonly clothSpinFriction: number;

  // --- Ball ↔ ball (consumed from PHY-003) ------------------------------------
  readonly ballBallRestitution: number;
  readonly ballBallFriction: number;

  // --- Ball ↔ cushion (consumed from PHY-004) ---------------------------------
  /** Restitution at low impact speed. */
  readonly cushionRestitutionLow: number;
  /** Restitution at high impact speed; interpolated between the two. */
  readonly cushionRestitutionHigh: number;
  /** Impact speed at which restitution reaches the high-speed value. */
  readonly cushionRestitutionSpeedMps: number;
  readonly cushionFriction: number;
  /** Cushion nose height as a fraction of ball DIAMETER (~0.635). */
  readonly cushionHeightRatio: number;
  /**
   * Resolution of a cushion impact: how many normal-impulse steps a frictionless
   * impact is divided into (PHY-007). Numerical, not a feel parameter — raise it and
   * nothing should change beyond the last few digits.
   */
  readonly cushionImpactSteps: number;

  // --- Pockets (consumed from PHY-005) ----------------------------------------
  /**
   * Multiplier on pocket mouth width. 1.0 is true to life. **1.1, decided by the owner
   * on 2026-09-25 (R-02)**: slightly generous, closer to how mobile pool plays, still
   * rewarding accuracy. Baked into every par, so changing it bumps physicsVersion.
   */
  readonly pocketScale: number;
  /**
   * Radius of the rounded jaw tip, in metres. A real jaw is not a knife edge: it is a
   * rubber nose with a radius, and that radius is what decides whether a ball clipping
   * the tip is deflected in or spat out. Not scaled by `pocketScale`, which is about
   * the mouth's width rather than the ironmongery around it.
   */
  readonly pocketJawTipRadiusM: number;

  // --- Cue strike -------------------------------------------------------------
  /** Cue ball speed at 100% power. */
  readonly maxCueSpeedMps: number;
  /** Tip offset cap as a fraction of R. Beyond ~0.5R a real cue miscues. */
  readonly maxTipOffsetR: number;

  // --- Rest -------------------------------------------------------------------
  /** Below this speed a ball is stopped dead, so shots terminate cleanly. */
  readonly restSpeedThresholdMps: number;
  /** Below this angular speed, spin is zeroed. Consumed from PHY-002. */
  readonly restSpinThresholdRadS: number;
}

export const DEFAULT_PHYSICS: PhysicsConfig = {
  dtS: 1 / 480,
  maxSimTimeS: 60,
  keyframeStride: 8,

  gravityMps2: 9.80665,
  ballRadiusM: 0.028575,
  ballMassKg: 0.17,

  clothSlidingFriction: 0.2,
  clothRollingResistance: 0.01,
  clothSpinFriction: 0.022,

  ballBallRestitution: 0.95,
  ballBallFriction: 0.05,

  cushionRestitutionLow: 0.85,
  cushionRestitutionHigh: 0.65,
  cushionRestitutionSpeedMps: 4,
  cushionFriction: 0.175,
  cushionHeightRatio: 0.635,
  cushionImpactSteps: 200,

  pocketScale: 1.1,
  pocketJawTipRadiusM: 0.012,

  maxCueSpeedMps: 7,
  maxTipOffsetR: 0.5,

  restSpeedThresholdMps: 0.005,
  restSpinThresholdRadS: 0.05,
};
